'use client';

/**
 * GlassSwitch Component
 *
 * A switch with glow effects when active.
 * Automatically adapts to light/dark theme.
 * Includes built-in security props for permission/tier/license locking.
 *
 * @see Issue #383 - Permission-Aware UI Components with Lock Icon Pattern
 */

import LockIcon from '@mui/icons-material/Lock';
import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch, { type SwitchProps } from '@mui/material/Switch';
import { alpha, styled, useTheme } from '@mui/material/styles';
import type { SecurityProps } from '@ops-matrix/shared-types';
import React, { forwardRef, useCallback } from 'react';
import { useSecurityLock } from '../../hooks/useSecurityLock';
import { getTierLabel } from '../../providers/PermissionProvider';
import { glowEffects } from '../../theme/shadows';
import { transitionPresets } from '../../theme/transitions';
import { iconSizes } from '../../theme/typography';
import { transientOptions } from '../../utils/styledUtils';
import { GlassChip } from '../GlassChip/GlassChip';
import { GlassTooltip } from '../GlassTooltip';

// ============================================================================
// Types
// ============================================================================

export interface GlassSwitchProps extends Omit<SwitchProps, 'onChange'>, SecurityProps {
  /**
   * Label text
   */
  label?: string;

  /**
   * Label placement
   * @default 'end'
   */
  labelPlacement?: 'end' | 'start' | 'top' | 'bottom';

  /**
   * Enable glow effect when checked
   * @default true
   */
  glowWhenChecked?: boolean;

  /**
   * Glow color variant
   * @default 'primary'
   */
  glowColor?: 'primary' | 'secondary' | 'mixed';

  /**
   * Switch size
   * @default 'medium'
   */
  size?: 'small' | 'medium';

  /**
   * Change handler
   */
  onChange?: (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void;
}

// ============================================================================
// Styled Components
// ============================================================================

interface StyledSwitchProps {
  $isDark: boolean;
  $glowWhenChecked: boolean;
  $glowColor: 'primary' | 'secondary' | 'mixed';
  $switchSize: 'small' | 'medium';
  $switchColor: SwitchProps['color'];
}

const getGlowValue = (color: 'primary' | 'secondary' | 'mixed', intensity: 'subtle' | 'medium'): string =>
  glowEffects[color][intensity];

const SWITCH_SIZES = {
  small: { width: 24, height: 14, thumb: 9, padding: 2 },
  medium: { width: 31, height: 18, thumb: 13, padding: 2 },
} as const;

const StyledSwitch = styled(
  Switch,
  transientOptions,
)<StyledSwitchProps>(({ theme, $isDark, $glowWhenChecked, $glowColor, $switchSize, $switchColor }) => {
  const colorKey = $switchColor === 'default' ? 'primary' : ($switchColor ?? 'primary');
  const mainColor = theme.palette[colorKey]?.main ?? theme.palette.primary.main;
  const s = SWITCH_SIZES[$switchSize];

  return {
    width: s.width,
    height: s.height,
    padding: 0,

    '& .MuiSwitch-switchBase': {
      padding: s.padding,
      transition: transitionPresets.button,

      '&.Mui-checked': {
        transform: `translateX(${s.width - s.thumb - s.padding * 2}px)`,
        color: '#fff',

        '& + .MuiSwitch-track': {
          // ── Checked track — Accent-tinted frosted glass ──
          background: `linear-gradient(135deg, ${alpha(mainColor, 0.55)} 0%, ${alpha(mainColor, 0.72)} 100%)`,
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          opacity: 1,
          border: `0.5px solid ${$isDark ? alpha(mainColor, 0.2) : 'rgba(255,255,255,0.35)'}`,
          ...($glowWhenChecked && {
            boxShadow: `${getGlowValue($glowColor, 'subtle')}, 0 2px 8px ${alpha(mainColor, 0.22)}`,
          }),
        },

        '& .MuiSwitch-thumb': {
          // Glass thumb with specular highlight
          background: 'linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(255,255,255,0.92) 100%)',
          boxShadow: `0 2px 8px rgba(0,0,0,0.25), 0 0 4px ${alpha(mainColor, 0.15)}, inset 0 1px 0 rgba(255,255,255,0.9)`,
        },
      },

      '&.Mui-disabled': {
        opacity: 0.5,
        '& + .MuiSwitch-track': {
          opacity: 0.3,
        },
      },
    },

    '& .MuiSwitch-thumb': {
      width: s.thumb,
      height: s.thumb,
      // ── Glass thumb — white with specular highlight ──
      background: 'linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(255,255,255,0.90) 100%)',
      boxShadow: '0 2px 6px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.9)',
      transition: transitionPresets.button,
    },

    '& .MuiSwitch-track': {
      borderRadius: s.height / 2,
      // ── Unchecked track — Frosted glass ──
      backgroundColor: $isDark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)',
      backdropFilter: 'blur(16px) saturate(150%)',
      WebkitBackdropFilter: 'blur(16px) saturate(150%)',
      border: `0.5px solid ${$isDark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)'}`,
      opacity: 1,
      transition: transitionPresets.button,
    },

    // Hover effect on the track
    '&:hover .MuiSwitch-track': {
      backgroundColor: $isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)',
      borderColor: $isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)',
    },

    '&:hover .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
      background: `linear-gradient(135deg, ${alpha(mainColor, 0.65)} 0%, ${alpha(mainColor, 0.82)} 100%)`,
    },
  };
});

// ============================================================================
// Component
// ============================================================================

export const GlassSwitch = forwardRef<HTMLButtonElement, GlassSwitchProps>(
  (
    {
      label,
      labelPlacement = 'end',
      glowWhenChecked = true,
      glowColor = 'primary',
      size = 'medium',
      color = 'primary',
      disabled,
      onChange,
      // Security props
      permission,
      anyPermission,
      allPermissions,
      requiredTier,
      requiredLicense,
      anyLicense,
      showLockIcon = true,
      showTierBadge = true,
      onLockedClick,
      lockedTooltip,
      ...props
    },
    ref,
  ) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    // Security lock state
    const { lockState, isLocked, handleLockedClick, lockedTooltipText } = useSecurityLock({
      permission,
      anyPermission,
      allPermissions,
      requiredTier,
      requiredLicense,
      anyLicense,
      onLockedClick,
      lockedTooltip,
    });

    const isDisabled = disabled || isLocked;

    // Handle change with security interception
    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
        if (isLocked) {
          e.preventDefault();
          e.stopPropagation();
          handleLockedClick();
          return;
        }
        onChange?.(e, checked);
      },
      [isLocked, handleLockedClick, onChange],
    );

    // Handle click on the locked area
    const handleLockedAreaClick = useCallback(() => {
      if (isLocked) {
        handleLockedClick();
      }
    }, [isLocked, handleLockedClick]);

    // Lock indicator
    const lockIndicator = isLocked && showLockIcon && lockState && (
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          ml: labelPlacement === 'end' ? 1 : 0,
          mr: labelPlacement === 'start' ? 1 : 0,
        }}
      >
        <LockIcon
          sx={{
            fontSize: iconSizes.sm,
            color:
              lockState.type === 'tier'
                ? theme.palette.warning.main
                : lockState.type === 'license'
                  ? theme.palette.info.main
                  : theme.palette.action.disabled,
          }}
        />
        {showTierBadge && lockState.type === 'tier' && lockState.requiredTier && (
          <GlassChip
            label={getTierLabel(lockState.requiredTier)}
            size="small"
            color="warning"
            sx={{
              height: 18,
              fontSize: '0.75rem',
              '& .MuiChip-label': { px: 0.5 },
            }}
          />
        )}
      </Box>
    );

    const switchElement = (
      <StyledSwitch
        ref={ref}
        color={color}
        $isDark={isDark}
        $glowWhenChecked={glowWhenChecked}
        $glowColor={glowColor}
        $switchSize={size}
        $switchColor={color}
        disabled={isDisabled}
        onChange={handleChange}
        {...props}
      />
    );

    // Build the full component
    const content = label ? (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: isLocked ? 'pointer' : 'default',
          opacity: isLocked ? 0.7 : 1,
        }}
        onClick={isLocked ? handleLockedAreaClick : undefined}
      >
        <FormControlLabel
          control={switchElement}
          label={
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <span>{label}</span>
              {lockIndicator}
            </Box>
          }
          labelPlacement={labelPlacement}
          sx={{
            marginLeft: 0,
            marginRight: 0,
            '& .MuiFormControlLabel-label': {
              fontSize: size === 'small' ? '0.8125rem' : '0.9375rem',
              color: isDisabled ? 'text.disabled' : 'text.primary',
            },
          }}
        />
      </Box>
    ) : (
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          cursor: isLocked ? 'pointer' : 'default',
        }}
        onClick={isLocked ? handleLockedAreaClick : undefined}
      >
        {switchElement}
        {lockIndicator}
      </Box>
    );

    // Wrap with tooltip if locked
    if (isLocked) {
      return (
        <GlassTooltip title={lockedTooltipText} placement="top">
          {content}
        </GlassTooltip>
      );
    }

    return content;
  },
);

GlassSwitch.displayName = 'GlassSwitch';

export default GlassSwitch;
